/**
 * BatchJob Model
 * @class
 */
import Netx from '@netx/core/bootstrap';
import Base from '@netx/core/lib/netx-model';
import customConfig from '../config';

class BatchJob extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {string} */
	get idAttribute() {
		return 'uuid';
	}

	/**
	 * @property {object}   defaults                             - The default attribute values
	 * @property {string}   defaults.completeUrl                 - The default batch job complete URL
	 * @property {string}   defaults.details                     - The default batch job details
	 * @property {string}   defaults.estimatedTime               - The defualt batch job estimated time
	 * @property {number}   defaults.increment                   - The defualt batch job increment
	 * @property {string}   defaults.jobTitle                    - The defualt batch job title
	 * @property {number}   defaults.percentComplete             - The defualt batch job percent complete
	 * @property {number}   defaults.runningTime                 - The defualt batch job running time
	 * @property {string}   defaults.runningTimeLabel            - The defualt batch job running time label
	 * @property {number}   defaults.secondsToReload             - The defualt batch job seconds to reload
	 * @property {number}   defaults.startTime                   - The defualt batch job start time
	 * @property {object}   defaults.shareData                   - The defualt batch job share data
	 * @property {boolean}  defaults.shareData.appendMetadata        - The defualt batch job share data append metadata
	 * @property {string}   defaults.shareData.errorCatasrophe       - The defualt batch job share data error catastrophe
	 * @property {string}   defaults.shareData.errorMessage          - The defualt batch job share data error message
	 * @property {number}   defaults.shareData.fileSize              - The defualt batch job share data file size
	 * @property {number}   defaults.shareData.hoursToLive           - The defualt batch job share data hours to live
	 * @property {array}    defaults.shareData.messages              - The defualt batch job share data messages
	 * @property {string}   defaults.shareData.name                  - The defualt batch job share data name
	 * @property {string}   defaults.shareData.path                  - The defualt batch job share data path
	 * @property {string}   defaults.shareData.realPath              - The defualt batch job share data real path
	 * @property {string}   defaults.shareData.size                  - The defualt batch job share data size
	 * @property {string}   defaults.shareData.sizeUncompressed      - The defualt batch job share data size uncompressed
	 * @property {string}   defaults.shareData.warningMessage        - The defualt batch job share data warning message
	 */
	get defaults() {
		return {
			completeUrl: '',
			details: '',
			estimatedTime: '',
			increment: 0,
			jobTitle: '',
			percentComplete: 0,
			runningTime: 0,
			runningTimeLabel: '',
			secondsToReload: 0,
			startTime: 0,
			shareData: {
				appendMetadata: false,
				errorCatastrophe: '',
				errorMessage: '',
				fileSize: 0,
				hoursToLive: 0,
				messages: [],
				name: '',
				path: '',
				realPath: '',
				size: '', // string
				sizeUncompressed: '', // string
				warningMessage: '',
			},

			// Client attributes
			args: [],
			hasResult: false,
		};
	}

	/** @type {Array} */
	get clientAttrs() {
		return ['args', 'hasResult'];
	}

	/**
	 * @memberof BatchJob
	 * @property {object} methods           - Model RPC methods
	 * @property {method} methods.read      - Fetch progress report
	 *
	 * @property {method} methods.getShare  - Fetch share bean
	 */
	get methods() {
		return {
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			read(opts) {
				// If the uuid isn't set, or is zero or undefined or whatever,
				// pass a 0 here (instead of zero or undefined or whatever.)
				return ['getProgressReport', this.id ? this.id : 0];
			},
			// Helpers
			getShare: ['getShareBean'],
		};
	}

	/**
	 * @property {object}   parsers           - The parsers available on this model
	 * @property {function} parsers.getShare  - Parses result of fetched share bean
	 */
	get parsers() {
		return {
			/**
			 * @function
			 * @param {object} data Share data
			 * @param {object} opts Request options
			 */
			getShare(data, opts) {
				this.set('shareData', data, opts);
			},
		};
	}

	/**
	 * @override
	 * @param {object} attrs Model attributes
	 * @param {object} opts Model options
	 */
	constructor(attrs, opts = {}) {
		super(attrs, opts);

		/**
		 * Promise for job completion
		 * @memberof module:app/models/batchJob
		 * @alias completed
		 * @type {object}
		 */
		this.completed = new $.Deferred();

		/**
		 * @memberof module:app/models/batchJob
		 * @alias eventStrs
		 * @type {object}
		 */
		this.eventStrs = opts.eventStrs || {};

		this.pollInterval = opts.pollInterval;

		// Hey, listen!
		this.listenTo(this, 'complete', (model) => {
			this.completed.resolveWith(this, [this]);
			this.destroy();
		}).listenTo(this, 'error', (model, error) => {
			this.completed.rejectWith(this, [error, this]);
		});

		// Wait until initialize is complete
		_.defer(this.trackJob.bind(this));
	}

	/**
	 * Add another event str to notify on batch job complete
	 * @method
	 * @param {string} eventStr Event string
	 * @param {array} args Event arguments
	 */
	addEventStr(eventStr, args) {
		this.eventStrs[eventStr] = args || [];
	}

	/**
	 * We do not have an RPC method - just want cleanup
	 * @override
	 * @param {object} opts Request options
	 */
	destroy(opts) {
		this.stopListening();
		this.trigger('destroy', this, this.collection, opts);
	}

	/**
	 * @method
	 * @fires module:app#app:pollBatchJob:messages
	 * @fires module:share#share:simpleDownload
	 */
	trackJob() {
		this.pollUntil(
			(status) => {
				this.trigger('progress', this, this.get('percentComplete'));
				return !status || this.get('percentComplete') >= 100;
			},
			null,
			null,
			this.pollInterval || customConfig.POLL_INTERVAL,
		).then(() => {
			// Expecting share data results
			if (this.get('hasResult')) {
				return this.getShare().then((shareData) => {
					shareData = shareData || {};

					// Completed
					this.triggerComplete([shareData]);

					// if the sharedata specifies a file, download that.
					if (shareData.path && shareData.path.length) {
						dispatcher.trigger('share:simpleDownload', shareData.path);
					}
				}, this.triggerError);
			}

			// Completed
			this.triggerComplete();
		}, this.triggerError.bind(this));
	}

	/**
	 * @method
	 * @param {string} error Error
	 */
	triggerError(error) {
		const args = this.get('args') || [];
		_.each(this.eventStrs, (eventArgs, eventStr) => {
			eventArgs = eventArgs || args;
			dispatcher.trigger.apply(
				dispatcher,
				[eventStr + ':error', error].concat(eventArgs),
			);
		});

		const errorMessage = error?.message?.includes('tiles generation failed')
			? i18n.t('i18n.assetZoomTilesUnavailable')
			: error;
		this.trigger.apply(this, ['error', this, errorMessage].concat(args));
		this.eventStrs = {};
	}

	/**
	 * @method
	 * @param {array} additionalArgs Additional arguments
	 */
	triggerComplete(additionalArgs) {
		additionalArgs = additionalArgs || [];
		const args = additionalArgs.concat(this.get('args') || []);
		_.each(this.eventStrs, (eventArgs, eventStr) => {
			eventArgs = eventArgs || args;
			// TODO: fix all places using `succeeded` and use `complete`
			dispatcher.trigger.apply(
				dispatcher,
				[eventStr + ':succeeded'].concat(eventArgs.concat(this)),
			);
		});
		this.trigger.apply(this, ['complete', this].concat(args));
		this.eventStrs = {};
	}

	/**
	 * @method
	 * @return {boolean} Rejected
	 */
	// note: this is currently only used for the checkin error of identical files
	isRejected() {
		// the uploaded file is identical to the current asset version
		const messageCode = 2103,
			progressMessages = this.get('progressMessages') || [],
			messageArr = progressMessages.filter(
				(message) => message.code === messageCode,
			);
		return messageArr.length > 0;
	}

	/** @type {string} */
	static get __name__() {
		return 'BatchJob';
	}
}

export default BatchJob;
